import { StorageService } from "./storage.service";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { map } from "rxjs/operators";
import { Uris } from "./Uris";

@Injectable({
  providedIn: "root"
})
export class PondService {
  constructor(
    public storageService: StorageService,
    private http: HttpClient,
    public router: Router
  ) {}

  getPonds(query: string = "", id: string | number = "") {
    const url = id ? `${Uris.PONDS}${id}/${query}` : `${Uris.PONDS}${query}`;
    return this.http.get(url, this.storageService.headers()).pipe(map((resp: any) => resp));
  }

  /**
   * Cambia el ciclo activo de un estanque, moviendo su siembra a otro ciclo.
   * @param pond_id - Id del estanque a mover de ciclo.
   * @param body - Ciclo destino y fechas de siembra/cosecha estimada.
   * @returns Observable con la respuesta del backend.
   */
  changePondCycle(
    pond_id: number,
    body: { cycle_id: number; planting_starting_date: string; planting_ending_date: string }
  ) {
    return this.http
      .post(
        `${Uris.PONDS}${pond_id}/change-pond-cycle/`,
        body,
        this.storageService.headersSoloAdmin()
      )
      .pipe(
        map((resp: any) => {
          this.storageService.animation(
            "success",
            "Éxito",
            "Se cambió el ciclo del estanque correctamente"
          );
          return resp;
        })
      );
  }

  /**
   * Habilita o deshabilita el manejo de la alimentacion por administracion (NAS-SI) en un estanque.
   * El campo es de solo lectura en el patch normal del estanque, por eso existe esta ruta dedicada.
   * @param id - Id del estanque.
   * @param value - Nuevo valor del booleano.
   * @returns Observable con el id del estanque y el valor aplicado.
   */
  patchPondFeedingByAdmin(id: string | number, value: boolean) {
    return this.http
      .patch(
        `${Uris.PONDS}${id}/feeding_by_admin/`,
        { feeding_by_admin: value },
        this.storageService.headersSoloAdmin()
      )
      .pipe(map((resp: any) => resp));
  }

  deletePond(id) {
    return this.http.delete(`${Uris.PONDS}${id}/`, this.storageService.headers()).pipe(
      map((resp: any) => {
        this.storageService.animation("success", "Éxito", `Estanque eliminado correctamente`);
        return resp;
      })
    );
  }

  getFeedingBehaviorGraph(id, query) {
    return this.http
      .get(`${Uris.PONDS}${id}/feeding-behavior-graph/${query}`, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }
  getFeedingFuzzyLogicGraph(query = "") {
    return this.http
      .get(
        `${Uris.FEEDINGREPORTS}feeding-fuzzy-logic-graph/${query}`,
        this.storageService.headers()
      )
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  postClientPond(value) {
    return this.http.post(`${Uris.PONDS}`, value, this.storageService.headers()).pipe(
      map((resp: any) => {
        this.storageService.animation(
          "success",
          "Éxito",
          `Estanque ${resp.name} creado correctamente`
        );
        return resp;
      })
    );
  }

  /**
   * Convierte un archivo KML en coordenadas de estanques.
   * @param value Objeto con el archivo en base64.
   * @returns Observable con la respuesta del backend que incluye los estanques parseados.
   */
  kml2ponds(value) {
    return this.http.post(`${Uris.FARM}kml2ponds/`, value, this.storageService.headers()).pipe(
      map((resp: any) => {
        this.storageService.animation("success", "Éxito", `Coordenadas correctamente insertadas`);
        return resp;
      })
    );
  }

  patchClientPond(value, id, alert: boolean = false) {
    return this.http
      .patch(`${Uris.PONDS}${Number(id)}/?all_records=true`, value, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          alert &&
            this.storageService.animation(
              "success",
              "Éxito",
              `Estanque ${resp.name} editado correctamente`
            );
          return resp;
        })
      );
  }

  trayData(id: any) {
    return this.http
      .get(`${Uris.PONDS}${id}/tray_data/`, this.storageService.headersSoloAdmin())
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  averagePondSensors(id: any) {
    return this.http
      .get(`${Uris.PONDS}${id}/sensor-record-avg/`, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  pondsComparison(query: string) {
    return this.http.get(`${Uris.PONDS_COMPARISON}${query}`, this.storageService.headers()).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }

  createTemplateMetric(body: any) {
    return this.http.post(`${Uris.PONDS_COMPARISON}`, body, this.storageService.headers()).pipe(
      map((resp: any) => {
        return resp;
      })
    );
  }

  patchTemplateMetric(id: number, body: any) {
    return this.http
      .patch(`${Uris.PONDS_COMPARISON}${id}/`, body, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  getDataComparison(query: string) {
    return this.http
      .get(`${Uris.PONDS_DATA_COMPARISON}${query}`, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }

  getDataComparisonGraph(query: string) {
    return this.http
      .get(`${Uris.PONDS_DATA_COMPARISON_GRAPH}${query}`, this.storageService.headers())
      .pipe(
        map((resp: any) => {
          return resp;
        })
      );
  }
}
